/**
 * Шапка групп колонок — полоса 22 px, sticky (§4.2).
 *
 * Клик по имени группы сворачивает её в одну колонку-индикатор 28 px.
 * Группа White Glove свёрнута по умолчанию: все три её поля пусты у всех
 * брендов (§0), и разворачивать её по умолчанию — значит отдать 360 px пустоте.
 */
import { useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { IconButton, IconSettings, Tooltip, useDragSort } from '../../../components/index.js'
import { GroupSettings } from './ColumnSettings.js'
import { tableTexts as t } from '../texts.table.js'
import { GROUP_HINT, groupTitles, type ColumnDef, type ColumnGroupCode } from './columns.js'
import { setGroupOrder } from './columnOrder.js'
import './GroupHeader.css'

export interface GroupHeaderProps {
  columns: ColumnDef[]
  collapsed: ReadonlySet<ColumnGroupCode>
  onToggle: (group: ColumnGroupCode) => void
  leftOffset: ReadonlyMap<string, number>
  rightOffset: ReadonlyMap<string, number>
  totalWidth: number
  /** Поиск по брендам: стоит в блоке «Бренд», справа от его имени. */
  search?: ReactNode
  /** Завести колонку в этом блоке: открывает конструктор в меню «Колонки». */
  onAddColumn?: (group: ColumnGroupCode) => void
}

interface Span {
  group: ColumnGroupCode
  width: number
  /** Закреплённая слева/справа группа едет вместе с колонками. */
  stickyLeft?: number
  stickyRight?: number
  key: string
}

export function GroupHeader({
  columns, collapsed, onToggle, leftOffset, rightOffset, totalWidth, search, onAddColumn,
}: GroupHeaderProps) {
  const [settings, setSettings] = useState<{ group: ColumnGroupCode; anchor: HTMLElement } | null>(null)
  const rowRef = useRef<HTMLDivElement | null>(null)
  /* Одна шапка на группу: раньше каждая закреплённая колонка получала свою,
     и над «Бренд · Видимость · Тир» стояло три одинаковых заголовка «Бренд». */
  const titles = groupTitles(columns)
  const spans: Span[] = []
  for (const column of columns) {
    const previous = spans[spans.length - 1]
    if (previous && previous.group === column.group) {
      previous.width += column.width
      continue
    }
    spans.push({ group: column.group, width: column.width, key: column.code })
  }

  /* Первая группа стоит над закреплёнными колонками, поэтому и сама закреплена:
     иначе её название уезжало при прокрутке, а колонки оставались. */
  const firstSpan = spans[0]
  if (firstSpan && leftOffset.size > 0) firstSpan.stickyLeft = 0

  /* Блоки переставляются прямо в шапке: взял имя блока, подержал — и он
     поехал вместе со своими колонками. */
  const drag = useDragSort({
    axis: 'x',
    items: spans.map((span) => span.group),
    /* Закреплённый слева блок остаётся на месте: на нём держится вся
       левая зона таблицы. */
    isFixed: (group) => group === spans[0]?.group,
    scroller: () => rowRef.current?.closest('.table__scroll') as HTMLElement | null,
    onDrop: (order) => setGroupOrder(order),
  })

  return (
    <div className="group-head" role="row" ref={rowRef} style={{ width: `${totalWidth}px` }}>
      {/* Фон шапки — липкая плашка шириной с видимую часть: сама строка шире
          экрана, и её фон обрезался краем области прокрутки прямым углом. */}
      <span className="head-plate head-plate--top" aria-hidden="true" />
      {/* Контур рисует отдельная плашка поверх ячеек: закреплённые слева
          колонки кладут свой фон поверх обычной плашки, и слева обводки
          просто не было видно. */}
      <span className="head-plate head-plate--top-frame" aria-hidden="true" />
      {spans.map((span) => {
        const title = titles[span.group]
        const classes = ['group-head__cell']
    if (title) classes.push('group-head__cell--named')
        if (span.stickyLeft !== undefined) classes.push('group-head__cell--sticky-left')
        if (span.stickyRight !== undefined) classes.push('group-head__cell--sticky-right')

        const style = {
          width: `${span.width}px`,
          ...(span.stickyLeft !== undefined ? { left: `${span.stickyLeft}px` } : null),
          ...(span.stickyRight !== undefined ? { right: `${span.stickyRight}px` } : null),
        }

        if (!title) return <div key={span.key} className={classes.join(' ')} style={style} aria-hidden="true" />
        if (drag.dragging === span.group) classes.push('group-head__cell--dragging')

        const hint = GROUP_HINT[span.group]
        /* Название группы — просто подпись: клик по нему сворачивал группу,
           и люди сворачивали её случайно. Видимость колонок живёт в «Колонках». */
        const button = <span className="group-head__btn t-group">{title}</span>

        return (
          <div
            key={span.key}
            ref={drag.register(span.group)}
            className={classes.join(' ')}
            style={style}
            onPointerDown={(event) => drag.onPointerDown(span.group, event)}
          >
            {hint ? <Tooltip content={hint}>{button}</Tooltip> : button}
            {span.group === 'base' ? search : null}
            {/* Шестерёнка блока: переименовать и завести в него колонку. */}
            <IconButton
              className="group-head__gear"
              data-no-drag
              label={t.columnSettings.openGroup(title)}
              icon={<IconSettings size={16} />}
              variant="ghost"
              size="s"
              inTable
              onClick={(event) => setSettings({ group: span.group, anchor: event.currentTarget })}
            />
          </div>
        )
      })}

      {drag.dropAt !== null ? (
        <span className="col-head__drop" aria-hidden="true" style={{ left: `${drag.dropAt}px` }} />
      ) : null}

      {settings ? (
        <GroupSettings
          group={settings.group}
          title={titles[settings.group] ?? settings.group}
          members={columns.filter((column) => column.group === settings.group)}
          anchor={settings.anchor}
          onClose={() => setSettings(null)}
          onAddColumn={() => onAddColumn?.(settings.group)}
        />
      ) : null}
    </div>
  )
}
